import type { MeResponse, StoreCapability } from '@avrahami/contracts';
import { Alert, Button, Centered as UiCentered, Loading, SIGNED_OUT } from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect, useId, type ReactNode } from 'react';
import { sessionApi, sessionQuery, useMe } from '../api/session';
import { he } from '../i18n/he';
import { can } from '../lib/access';
import { errorMessage } from '../lib/forms';
import { homePath, NAV_GROUPS, visibleNav } from '../lib/nav';
import { announceTo, sessionChanged } from '../lib/session-sync';
import { goToStore, tabStore } from '../lib/store-path';

/** Full-page frame for the screens outside the signed-in app (sign-in, invitation). */
export function Centered({ children }: { children: ReactNode }) {
  return <UiCentered product={he.app.product}>{children}</UiCentered>;
}

/** Signing out: the server ends the session, and nothing of this store stays in memory. */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sessionApi.logout,
    onSettled: () => {
      sessionChanged(queryClient, SIGNED_OUT);
    },
  });
}

/**
 * Moving to another store of the member: the store gets a session of its own (adr/0033) and this
 * tab loads again at its address. The tabs of the store left keep their session; the tabs of the
 * store entered re-read theirs.
 */
export function useSwitchTenant() {
  return useMutation({
    mutationFn: sessionApi.openStore,
    onSuccess: (me) => {
      const store = me.activeTenant;
      if (store === null) return;
      // This tab stays on its store until the new address loads; only the other store's tabs hear.
      announceTo([store.tenantId]);
      goToStore(store.tenantId, homePath(store.capabilities));
    },
  });
}

/**
 * A tab without a store (adr/0033) on a screen of the app, as an old link or bookmark opens one:
 * when this browser is signed in to exactly one store, that store's address opens; otherwise the
 * sign-in screen offers the stores that are open, or a new sign-in.
 */
function useStoreOfRootTab(asked: boolean) {
  const sessions = useQuery({
    queryKey: ['store-sessions'],
    queryFn: sessionApi.storeSessions,
    enabled: asked,
    retry: false,
    staleTime: 0,
  });
  const stores = sessions.data?.stores;
  const only = stores?.length === 1 ? stores[0] : undefined;
  useEffect(() => {
    if (only === undefined) return;
    goToStore(only.tenantId, `${window.location.pathname}${window.location.search}`, true);
  }, [only]);
  return { settled: !asked || sessions.isError || (stores !== undefined && only === undefined) };
}

function StoreSwitcher({ me }: { me: MeResponse }) {
  const id = useId();
  const switchTenant = useSwitchTenant();
  if (me.memberships.length < 2 || me.activeTenant === null) {
    return <span className="font-bold text-brand-900">{me.activeTenant?.displayName}</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-xs font-medium text-slate-500">
        <span className="sr-only">{he.switcher.label}, </span>
        {he.switcher.visible(me.memberships.length)}
      </label>
      <select
        id={id}
        data-testid="store-switcher"
        value={me.activeTenant.tenantId}
        disabled={switchTenant.isPending}
        onChange={(event) => {
          switchTenant.mutate(event.target.value);
        }}
        className="rounded-md border border-brand-600 bg-brand-50 px-2 py-1 text-sm font-semibold text-brand-900"
      >
        {me.memberships.map((membership) => (
          <option key={membership.tenantId} value={membership.tenantId}>
            {membership.displayName}
          </option>
        ))}
      </select>
      {switchTenant.isError ? (
        <span role="alert" className="text-xs text-red-600">
          {errorMessage(switchTenant.error)}
        </span>
      ) : null}
    </div>
  );
}

function SideNav({ capabilities }: { capabilities: readonly string[] }) {
  const items = visibleNav(capabilities);
  return (
    <nav aria-label={he.nav.menu} className="flex flex-col gap-4">
      {NAV_GROUPS.map((group) => {
        const inGroup = items.filter((item) => item.group === group);
        if (inGroup.length === 0) return null;
        return (
          <div key={group} className="flex flex-col gap-1">
            <p className="px-3 text-xs font-semibold text-slate-400">{he.navGroups[group]}</p>
            {inGroup.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`nav-${item.key}`}
                activeOptions={{ exact: item.to === '/' }}
                className="rounded-md px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                activeProps={{ className: 'bg-brand-50 font-semibold text-brand-900' }}
              >
                {he.nav[item.key]}
              </Link>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

/**
 * The signed-in shell. It decides, from the session, where the member belongs: sign-in, the
 * second step, "no store", or the app itself. The API enforces all of this again; the shell only
 * avoids showing screens that would fail.
 */
export function AppLayout() {
  const navigate = useNavigate();
  const session = useQuery(sessionQuery);
  const logout = useLogout();

  const state = session.data;
  const active = state?.kind === 'signed-in' ? state.me.activeTenant : null;
  // A session in a store belongs at that store's address (adr/0033).
  const elsewhere = active !== null && active.tenantId !== tabStore ? active : null;
  const rootTab = useStoreOfRootTab(
    tabStore === undefined &&
      state !== undefined &&
      state.kind !== 'mfa-pending' &&
      active === null,
  );
  const destination =
    state === undefined || !rootTab.settled
      ? null
      : state.kind !== 'signed-in'
        ? 'login'
        : state.me.activeTenant === null
          ? 'no-store'
          : null;
  useEffect(() => {
    if (elsewhere === null) return;
    const path = tabStore === undefined ? window.location.pathname : '/';
    goToStore(elsewhere.tenantId, path, true);
  }, [elsewhere]);
  // Runs when the answer changes, not on every render: the address is read once, so the
  // redirect back after signing in cannot feed on itself.
  useEffect(() => {
    if (destination === 'login') {
      const back = `${window.location.pathname}${window.location.search}`;
      void navigate({ to: '/login', search: { redirect: back }, replace: true });
    } else if (destination === 'no-store') {
      void navigate({ to: '/no-store', replace: true });
    }
  }, [destination, navigate]);

  if (session.isError) {
    return (
      <Centered>
        <Alert>{errorMessage(session.error)}</Alert>
      </Centered>
    );
  }
  if (state?.kind !== 'signed-in' || destination !== null || active === null || elsewhere) {
    return (
      <Centered>
        <Loading />
      </Centered>
    );
  }
  const { me } = state;

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
          <span className="text-sm text-slate-500">{he.app.title}</span>
          <StoreSwitcher me={me} />
          <span className="flex-1" />
          <span className="ltr text-xs text-slate-500">{me.user.email}</span>
          <Button
            tone="secondary"
            busy={logout.isPending}
            onClick={() => {
              logout.mutate();
            }}
          >
            {he.nav.logout}
          </Button>
        </div>
      </header>
      {active.managedByPlatform ? (
        <div className="mx-auto max-w-7xl px-4 pt-4" data-testid="managed-banner">
          <Alert tone="info">{he.managed.banner}</Alert>
        </div>
      ) : null}
      {active.status === 'SUSPENDED' ? (
        <div className="mx-auto max-w-7xl px-4 pt-4" data-testid="suspended-banner">
          <Alert tone="warning">{he.suspended.banner}</Alert>
        </div>
      ) : null}
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 md:flex-row">
        <aside className="md:w-52 md:shrink-0">
          <SideNav capabilities={active.capabilities} />
        </aside>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** A screen the role cannot use explains itself instead of failing on every call. */
export function RequireCapability({
  needs,
  children,
}: {
  needs: readonly StoreCapability[];
  children: ReactNode;
}) {
  const me = useMe();
  if (me === undefined) return <Loading />;
  if (!needs.every((capability) => can(me, capability))) {
    return <Alert tone="info">{he.common.noPermission}</Alert>;
  }
  return children;
}
